import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import StackedDotswithBars from '../StackedDotswithBars';
import { useContext, useMemo, useRef, useState } from 'react';
import { groupBarChartWidth } from 'components/organism/PVMAnalysis/PVMInfiniteImpactChartContainer';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import useDepletionvsMarginData from './hooks/useDepletionvsMarginData';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { isEmpty } from 'lodash';

const DepletionVsMarginChart = () => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { kpiLabelMapping, marginPercentChartColors } = profitPoolConfig;

    const currentRef = useRef<any>(null);
    const containerWidth2 = useContainerWidth({
        containerId: 'depletion-vs-margin-chart',
    });
    const chartHeight = calculateContainerHeight(3);

    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2);
    const [toLeft, setToLeft] = useState(0);

    const { multiSelectedTiers, profitPoolTierList, allDimAndAttributeTypeList } = useContext(FilterContextData);
    const pointsColorMap: any = {
        CBI: '#826FD0',
        CBBD: '#826FD0',
        Distributor: '#4CBC38',
        Retailer: '#FF791A',
    };

    const {
        data: depletionVsMarginData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        isFetchingNextPage,
        minMarginPercent,
        maxMarginPercent,
        minDepletionVolume,
        maxDepletionVolume,
    } = useDepletionvsMarginData();

    // Prepare Excel export data
    const excelExportData = useMemo(() => {
        if (!depletionVsMarginData?.length) {
            return { headers: [], data: [] };
        }

        const headers = [
            'Period',
            'Tier',
            'Margin %',
            kpiLabelMapping['volume_sales_equivalent'] || 'Depletion Volume',
        ];

        const data = depletionVsMarginData.map((item: any) => [
            item.periodLabel || '',
            item.tier || '',
            item.marginPercent || 0,
            item['Depletion Volume'] || 0,
        ]);

        return { headers, data };
    }, [depletionVsMarginData, kpiLabelMapping]);

    if (isPending || allDimAndAttributeTypeList?.loading) {
        return (
            <>
                <p className="text-lg font-semibold">{'Depletions vs %Margin'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </>
        );
    }

    if (isError) {
        return (
            <>
                <p className="text-lg font-semibold">{'Depletions vs %Margin'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </>
        );
    }

    if (isSuccess && isEmpty(depletionVsMarginData)) {
        return (
            <>
                <p className="text-lg font-semibold">{'Depletions vs %Margin'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{'Depletions vs %Margin'}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <CopyToClipBoard refOfComponent={currentRef} />
                    <DownloadExcel
                        title={'Depletions vs %Margin'}
                        excelData={excelExportData.data}
                        headers={excelExportData.headers}
                    />
                </div>
            </div>

            {/* Legends */}
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <>
                    <div className="text-sm">
                        <p>{'Margin %'}</p>
                    </div>
                    <div className="flex flex-row gap-2 text-xs">
                        <div
                            className="w-3 h-2 self-center"
                            style={{
                                backgroundColor: '#F6D9A7',
                            }}
                        />

                        <p>{kpiLabelMapping?.['volume_sales_equivalent']}</p>

                        {multiSelectedTiers.map((tier, index) => {
                            return (
                                <>
                                    <div
                                        className="w-3 h-2 self-center"
                                        style={{
                                            backgroundColor:
                                                pointsColorMap[
                                                    profitPoolTierList.find((t) => t.tierReference === tier)?.tierName
                                                ],
                                        }}
                                    />

                                    <p>{`${profitPoolTierList.find((t) => t.tierReference === tier)?.tierName}`}</p>
                                </>
                            );
                        })}
                    </div>
                    <div>
                        <p className="text-sm">{kpiLabelMapping?.['volume_sales_equivalent']}</p>
                    </div>
                </>
            </div>

            {/* Chart */}
            <section ref={currentRef}>
                <div className="flex">
                    <div className="">
                        <Barchart
                            padding={[0, 100, 15, 0]}
                            barWidth={0}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`depletion-vs-margin-chart-y1-axis`}
                            fields={['periodLabel', 'marginPercent', 'tier']}
                            minBound={minMarginPercent < 0 ? minMarginPercent : 0}
                            maxBound={scaleMaxValue(20, maxMarginPercent)}
                            width={70}
                            colorKey={'tier'}
                            YGrid={false}
                            yAxisConfig={true}
                            yAxisLabelFormatter={(val: any) => {
                                return `${val}%`;
                            }}
                        />
                    </div>
                    <HorizontalInfiniteScroller
                        id={`depletion-vs-margin-chart-container`}
                        hasMore={true}
                        scrollThreshold={0.9}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            // fetchNextPage();
                        }}
                    >
                        <div id="depletion-vs-margin-chart-container" className="overflow-x-auto max-w-full pb-[60px]">
                            <StackedDotswithBars
                                containerName="depletion-vs-margin-chart-stacked-dots-with-bars"
                                dataSet={depletionVsMarginData as any}
                                loading={isFetchingNextPage}
                                height={chartHeight}
                                width={graphViewContainerWidth3 * Math.ceil(depletionVsMarginData.length / 45) * 1}
                                xField={'periodLabel'}
                                pointsField="marginPercent"
                                pointMin={minMarginPercent < 0 ? minMarginPercent : 0}
                                pointMax={scaleMaxValue(20, maxMarginPercent)}
                                pointColorField={'tier'}
                                barField="Depletion Volume"
                                barMin={minDepletionVolume < 0 ? minDepletionVolume : 0}
                                barMax={scaleMaxValue(20, maxDepletionVolume)}
                                pointsColorMap={pointsColorMap}
                                y2Type="line"
                                isPointPercentKPI={true}
                            />
                        </div>
                    </HorizontalInfiniteScroller>
                    <div className="">
                        <Barchart
                            padding={[0, 0, 15, 20]}
                            barWidth={10}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`depletion-vs-margin-chart-y2-axis`}
                            fields={['periodLabel', 'Depletion Volume', 'tier']}
                            minBound={minDepletionVolume < 0 ? minDepletionVolume : 0}
                            maxBound={scaleMaxValue(20, maxDepletionVolume)}
                            width={70}
                            colorKey={'tier'}
                            YGrid={false}
                            yAxisConfig={true}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val);
                            }}
                        />
                    </div>
                </div>
            </section>
        </>
    );
};

export default DepletionVsMarginChart;
